import { useCallback } from 'react';
import { TaskItem } from '@demind-inc/core';

import { useRecoilState } from 'recoil';

import {
  eventsSnackBarAtom,
  useAuthContext,
  useTodoTasksContext,
  useMoveTodoTaskBetweenProjects,
  useAddTodoTask,
  useDeleteTask,
} from '../data-access';
import {
  isAppleRemindersDesktopSupported,
  createAppleReminderDesktop,
  updateAppleReminderDesktop,
  deleteAppleReminderDesktop,
  mapTaskPriorityToApplePriority,
  recurrenceStringToAppleRule,
} from '../helpers/appleRemindersDesktop.helpers';
import {
  isCustomRecurrence,
  normalizeRecurringString,
} from '../components/TaskDetailModal/TaskScheduleSection/recurrenceHelpers';

export interface MoveTaskBetweenProjectsParams {
  task: TaskItem;
  targetBoardId: string;
}

export const useMoveTaskBetweenProjects = () => {
  const { user } = useAuthContext();
  const { findProject } = useTodoTasksContext();
  const { moveTodoTaskBetweenProjects, isPending: isMovePending } =
    useMoveTodoTaskBetweenProjects();
  const { addTodoTask } = useAddTodoTask();
  const { deleteTask: deleteTodoTask } = useDeleteTask();
  const [, setEventsSnackbar] = useRecoilState(eventsSnackBarAtom);

  const moveTaskBetweenProjects = useCallback(
    async ({ task, targetBoardId }: MoveTaskBetweenProjectsParams) => {
      if (!user?.userId) {
        return false;
      }

      if (!task.boardId || !task.taskId) {
        return false;
      }

      if (task.boardId === targetBoardId) {
        return false;
      }

      const sourceProject = findProject(task.boardId);
      const targetProject = findProject(targetBoardId);

      if (!targetProject) {
        console.error('Target project not found for boardId:', targetBoardId);
        return false;
      }

      const isSourceAppleReminders = sourceProject?.appFrom === 'apple-reminders';
      const isTargetAppleReminders = targetProject?.appFrom === 'apple-reminders';
      const isCrossProvider = sourceProject?.appFrom !== targetProject?.appFrom;

      const normalizedRecurrence = normalizeRecurringString(task.recurring);
      if (normalizedRecurrence && isCustomRecurrence(normalizedRecurrence) && isCrossProvider) {
        return false;
      }

      try {
        // Apple -> Apple: update the list on the native reminder (preserves recurrence/alarms),
        // then delete+create on the backend
        if (
          isSourceAppleReminders &&
          isTargetAppleReminders &&
          isAppleRemindersDesktopSupported()
        ) {
          if (task.originalTaskId) {
            await updateAppleReminderDesktop({
              reminderId: task.originalTaskId,
              title: task.name || '',
              listIdentifier: targetProject.originalBoardId,
            });
          }

          const {
            taskId: _ignoreTaskId,
            originalTaskId: _ignoreOriginalTaskId,
            boardId: _sourceBoardId,
            ...rest
          } = task;

          await addTodoTask({
            boardId: targetBoardId,
            userId: user.userId,
            newTaskInfo: { ...rest, appFrom: 'apple-reminders' },
            originalTaskId: task.originalTaskId,
          });

          await deleteTodoTask({
            taskId: task.taskId,
            boardId: task.boardId,
            userId: user.userId,
          });

          setEventsSnackbar({ message: `Task moved to ${targetProject.name}`, type: 'success', icon: 'task' });
          return true;
        }

        // Non-Apple -> Apple: create native reminder, delete+create on backend
        if (isTargetAppleReminders && isAppleRemindersDesktopSupported()) {
          const hasDateOnly = !!task.dueDateTime?.date && !task.dueDateTime?.datetime;
          const dueDate = task.dueDateTime?.datetime || task.dueDateTime?.date;
          const applePriority = mapTaskPriorityToApplePriority(task.priority);
          const appleRecurrenceRule = recurrenceStringToAppleRule(task.recurring?.string);

          const createdReminder = await createAppleReminderDesktop({
            title: task.name || '',
            notes: task.desc,
            ...(dueDate && { dueDate, allDay: hasDateOnly }),
            priority: applePriority,
            listIdentifier: targetProject.originalBoardId,
            ...(appleRecurrenceRule && { recurrenceRule: appleRecurrenceRule }),
          });

          if (!createdReminder?.id) {
            throw new Error("Couldn't create that reminder");
          }

          const {
            taskId: _ignoreTaskId,
            originalTaskId: _ignoreOriginalTaskId,
            boardId: _sourceBoardId,
            ...rest
          } = task;

          await addTodoTask({
            boardId: targetBoardId,
            userId: user.userId,
            newTaskInfo: { ...rest, appFrom: 'apple-reminders' },
            originalTaskId: createdReminder.id,
          });

          await deleteTodoTask({
            taskId: task.taskId,
            boardId: task.boardId,
            userId: user.userId,
          });

          setEventsSnackbar({ message: `Task moved to ${targetProject.name}`, type: 'success', icon: 'task' });
          return true;
        }

        // Apple -> non-Apple: use move API, then delete native reminder
        if (isSourceAppleReminders && isAppleRemindersDesktopSupported()) {
          await moveTodoTaskBetweenProjects({
            userId: user.userId,
            taskId: task.taskId,
            sourceBoardId: task.boardId,
            targetBoardId,
          });

          if (task.originalTaskId) {
            await deleteAppleReminderDesktop(task.originalTaskId);
          }

          setEventsSnackbar({ message: `Task moved to ${targetProject.name}`, type: 'success', icon: 'task' });
          return true;
        }

        // Non-Apple -> non-Apple: standard move API
        await moveTodoTaskBetweenProjects({
          userId: user.userId,
          taskId: task.taskId,
          sourceBoardId: task.boardId,
          targetBoardId,
        });

        setEventsSnackbar({ message: `Task moved to ${targetProject.name}`, type: 'success', icon: 'task' });
        return true;
      } catch (error) {
        setEventsSnackbar({ message: "Couldn't move that task — try again?", type: 'error', icon: 'task' });
        return false;
      }
    },
    [
      user?.userId,
      findProject,
      moveTodoTaskBetweenProjects,
      addTodoTask,
      deleteTodoTask,
      setEventsSnackbar,
    ]
  );

  return {
    moveTaskBetweenProjects,
    isMovingTaskBetweenProjects: isMovePending,
  };
};
